import React, { useState, useEffect } from 'react';
import { Vehicle, CreateVehicleDto } from '../../types/vehicle';

interface VehicleFormProps {
  vehicle?: Vehicle | null;
  onSubmit: (vehicleData: CreateVehicleDto) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const VehicleForm: React.FC<VehicleFormProps> = ({
  vehicle,
  onSubmit,
  onCancel,
  isLoading = false
}) => {
  const [formData, setFormData] = useState<CreateVehicleDto>({
    name: '',
    brand: '',
    model: '',
    year: new Date().getFullYear(),
    kmh: 0,
    description: '',
    isAutomatic: true,
    defaultRatePerDay: 0,
    defaultUnlimitedRatePerDay: 0,
    rates: [
      { title: '1 day', rate: 0, minDays: 1, maxDays: 1 },
      { title: '2 days', rate: 0, minDays: 2, maxDays: 2 },
      { title: '3-6 days', rate: 0, minDays: 3, maxDays: 6 },
      { title: '7+ days', rate: 0, minDays: 7, maxDays: null }
    ],
    unlimedRates: [
      { title: '1 day', rate: 0, minDays: 1, maxDays: 1 },
      { title: '2 days', rate: 0, minDays: 2, maxDays: 2 },
      { title: '3-6 days', rate: 0, minDays: 3, maxDays: 6 },
      { title: '7+ days', rate: 0, minDays: 7, maxDays: null }
    ],
    seats: 4,
    bags: 2,
    totalQuantity: 1,
    licensePlate: '',
    vin: '',
    color: '',
    fuelType: '',
    location: '',
    serviceIntervalMiles: 10000,
    serviceIntervalDays: 365,
    purchaseDate: undefined,
    purchasePrice: undefined,
    insuranceProvider: '',
    insurancePolicyNumber: '',
    insuranceExpiryDate: undefined,
    registrationNumber: '',
    registrationExpiryDate: undefined
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (vehicle) {
      setFormData({
        name: vehicle.name,
        brand: vehicle.brand,
        model: vehicle.model,
        year: vehicle.year,
        kmh: vehicle.kmh,
        description: vehicle.description || '',
        isAutomatic: vehicle.isAutomatic,
        defaultRatePerDay: vehicle.defaultRatePerDay,
        defaultUnlimitedRatePerDay: vehicle.defaultUnlimitedRatePerDay,
        rates: vehicle.rates || [],
        unlimedRates: vehicle.unlimedRates || [],
        seats: vehicle.seats,
        bags: vehicle.bags,
        totalQuantity: vehicle.totalQuantity,
        licensePlate: vehicle.licensePlate || '',
        vin: vehicle.vin || '',
        color: vehicle.color || '',
        fuelType: vehicle.fuelType || '',
        location: vehicle.location || '',
        serviceIntervalMiles: vehicle.serviceIntervalMiles,
        serviceIntervalDays: vehicle.serviceIntervalDays,
        purchaseDate: vehicle.purchaseDate ? new Date(vehicle.purchaseDate) : undefined,
        purchasePrice: vehicle.purchasePrice,
        insuranceProvider: vehicle.insuranceProvider || '',
        insurancePolicyNumber: vehicle.insurancePolicyNumber || '',
        insuranceExpiryDate: vehicle.insuranceExpiryDate ? new Date(vehicle.insuranceExpiryDate) : undefined,
        registrationNumber: vehicle.registrationNumber || '',
        registrationExpiryDate: vehicle.registrationExpiryDate ? new Date(vehicle.registrationExpiryDate) : undefined
      });
    }
  }, [vehicle]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : 
              type === 'checkbox' ? (e.target as HTMLInputElement).checked :
              type === 'date' ? (value ? new Date(value) : undefined) :
              value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handlePricingTierChange = (index: number, field: 'limited' | 'unlimited', value: number) => {
    setFormData(prev => {
      const arrayName = field === 'limited' ? 'rates' : 'unlimedRates';
      const updatedRates = [...(prev[arrayName] || [])];
      updatedRates[index] = { ...updatedRates[index], rate: value };
      
      // Update the default rates based on the 7+ days tier (longest rental = best rate)
      // This is used as the "base" rate for calculations
      if (updatedRates[index].minDays === 7) {
        if (field === 'limited') {
          return { ...prev, [arrayName]: updatedRates, defaultRatePerDay: value };
        } else {
          return { ...prev, [arrayName]: updatedRates, defaultUnlimitedRatePerDay: value };
        }
      }
      
      return { ...prev, [arrayName]: updatedRates };
    });
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) newErrors.name = 'Vehicle name is required';
    if (!formData.brand.trim()) newErrors.brand = 'Brand is required';
    if (!formData.model.trim()) newErrors.model = 'Model is required';
    if (formData.year < 1900 || formData.year > new Date().getFullYear() + 1) {
      newErrors.year = 'Please enter a valid year';
    }
    if (formData.defaultRatePerDay <= 0) newErrors.defaultRatePerDay = 'Daily rate must be greater than 0';
    if (formData.defaultUnlimitedRatePerDay <= 0) newErrors.defaultUnlimitedRatePerDay = 'Unlimited daily rate must be greater than 0';
    if (formData.seats < 1) newErrors.seats = 'Seats must be at least 1';
    if (formData.bags < 0) newErrors.bags = 'Bags cannot be negative';
    if (formData.totalQuantity < 1) newErrors.totalQuantity = 'Quantity must be at least 1';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      // Clean rates - remove _id fields and ensure proper structure
      const cleanRates = (rates: any[] | undefined) => (rates || []).map((rate: any) => ({
        title: rate.title,
        rate: rate.rate,
        minDays: rate.minDays,
        maxDays: rate.maxDays
      }));

      const updatedRates = cleanRates(formData.rates);
      const updatedUnlimedRates = cleanRates(formData.unlimedRates);

      // Build clean data object with only the fields the API expects
      const cleanedData: Partial<CreateVehicleDto> = {
        name: formData.name,
        brand: formData.brand,
        model: formData.model,
        year: formData.year || new Date().getFullYear(),
        kmh: formData.kmh || 0,
        description: formData.description || '',
        isAutomatic: formData.isAutomatic,
        defaultRatePerDay: formData.defaultRatePerDay,
        defaultUnlimitedRatePerDay: formData.defaultUnlimitedRatePerDay,
        rates: updatedRates,
        unlimedRates: updatedUnlimedRates,
        seats: formData.seats,
        bags: formData.bags,
        totalQuantity: formData.totalQuantity,
        licensePlate: formData.licensePlate || '',
        vin: formData.vin || '',
        color: formData.color || '',
        fuelType: formData.fuelType || '',
        location: formData.location || '',
        serviceIntervalMiles: formData.serviceIntervalMiles || 0,
        serviceIntervalDays: formData.serviceIntervalDays || 0,
        insuranceProvider: formData.insuranceProvider || '',
        insurancePolicyNumber: formData.insurancePolicyNumber || '',
        registrationNumber: formData.registrationNumber || ''
      };

      // Only add optional date fields if they have values
      if (formData.purchaseDate) {
        cleanedData.purchaseDate = formData.purchaseDate;
      }
      if (formData.purchasePrice) {
        cleanedData.purchasePrice = formData.purchasePrice;
      }
      if (formData.insuranceExpiryDate) {
        cleanedData.insuranceExpiryDate = formData.insuranceExpiryDate;
      }
      if (formData.registrationExpiryDate) {
        cleanedData.registrationExpiryDate = formData.registrationExpiryDate;
      }

      onSubmit(cleanedData as CreateVehicleDto);
    }
  };

  const formatDateForInput = (date: Date | undefined): string => {
    if (!date) return '';
    return date.toISOString().split('T')[0];
  };

  return (
    <div className="bg-white rounded-lg shadow-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900">
          {vehicle ? 'Edit Vehicle' : 'Add New Vehicle'}
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Basic Information */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vehicle Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="e.g., Toyota Camry 2023"
              />
              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Brand *
              </label>
              <input
                type="text"
                name="brand"
                value={formData.brand}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.brand ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="e.g., Toyota"
              />
              {errors.brand && <p className="text-red-500 text-sm mt-1">{errors.brand}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Model *
              </label>
              <input
                type="text"
                name="model"
                value={formData.model}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.model ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="e.g., Camry"
              />
              {errors.model && <p className="text-red-500 text-sm mt-1">{errors.model}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Year *
              </label>
              <input
                type="number"
                name="year"
                value={formData.year}
                onChange={handleInputChange}
                min="1900"
                max={new Date().getFullYear() + 1}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.year ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.year && <p className="text-red-500 text-sm mt-1">{errors.year}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Color
              </label>
              <input
                type="text"
                name="color"
                value={formData.color}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., White"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fuel Type
              </label>
              <select
                name="fuelType"
                value={formData.fuelType}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select fuel type</option>
                <option value="Gasoline">Gasoline</option>
                <option value="Diesel">Diesel</option>
                <option value="Hybrid">Hybrid</option>
                <option value="Electric">Electric</option>
                <option value="Plug-in Hybrid">Plug-in Hybrid</option>
              </select>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Vehicle description..."
            />
          </div>
        </div>

        {/* Vehicle Specifications */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Vehicle Specifications</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Seats *
              </label>
              <input
                type="number"
                name="seats"
                value={formData.seats}
                onChange={handleInputChange}
                min="1"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.seats ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="e.g., 5"
              />
              {errors.seats && <p className="text-red-500 text-sm mt-1">{errors.seats}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bags/Luggage *
              </label>
              <input
                type="number"
                name="bags"
                value={formData.bags}
                onChange={handleInputChange}
                min="0"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.bags ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="e.g., 2"
              />
              {errors.bags && <p className="text-red-500 text-sm mt-1">{errors.bags}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Transmission *
              </label>
              <select
                name="isAutomatic"
                value={formData.isAutomatic ? 'true' : 'false'}
                onChange={(e) => setFormData(prev => ({ ...prev, isAutomatic: e.target.value === 'true' }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="true">Automatic</option>
                <option value="false">Manual</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fleet Quantity *
              </label>
              <input
                type="number"
                name="totalQuantity"
                value={formData.totalQuantity}
                onChange={handleInputChange}
                min="1"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.totalQuantity ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="e.g., 3"
              />
              {errors.totalQuantity && <p className="text-red-500 text-sm mt-1">{errors.totalQuantity}</p>}
            </div>
          </div>
          
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Brief description of the vehicle features and condition..."
            />
          </div>
        </div>

        {/* Pricing Tiers */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Pricing Tiers</h3>
          <p className="text-sm text-gray-600 mb-4">Set daily rates for different rental durations</p>
          
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Duration
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Limited Mileage ($/day)
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Unlimited Mileage ($/day)
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {formData.rates?.map((tier, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {tier.title}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="relative">
                          <span className="absolute left-3 top-2 text-gray-500 text-sm">$</span>
                          <input
                            type="number"
                            value={tier.rate}
                            onChange={(e) => handlePricingTierChange(index, 'limited', parseFloat(e.target.value) || 0)}
                            min="0"
                            step="0.01"
                            className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            placeholder="0.00"
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="relative">
                          <span className="absolute left-3 top-2 text-gray-500 text-sm">$</span>
                          <input
                            type="number"
                            value={formData.unlimedRates?.[index]?.rate || 0}
                            onChange={(e) => handlePricingTierChange(index, 'unlimited', parseFloat(e.target.value) || 0)}
                            min="0"
                            step="0.01"
                            className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            placeholder="0.00"
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 text-xs text-gray-500">
              <p>💡 Tip: Set lower rates for longer rentals to encourage extended bookings</p>
            </div>
          </div>
          
          {(errors.defaultRatePerDay || errors.defaultUnlimitedRatePerDay) && (
            <p className="text-red-500 text-sm mt-2">
              Please set all pricing tiers (7+ days rate is required)
            </p>
          )}
        </div>

        {/* Fleet Information - Collapsible */}
        <details className="border border-gray-200 rounded-lg">
          <summary className="px-4 py-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Fleet Information (Optional)</h3>
            <span className="text-gray-500 text-sm">Click to expand</span>
          </summary>
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                License Plate
              </label>
              <input
                type="text"
                name="licensePlate"
                value={formData.licensePlate}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., ABC-123"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                VIN
              </label>
              <input
                type="text"
                name="vin"
                value={formData.vin}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Vehicle Identification Number"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Downtown Branch"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Purchase Date
              </label>
              <input
                type="date"
                name="purchaseDate"
                value={formatDateForInput(formData.purchaseDate)}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Purchase Price
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-500">$</span>
                <input
                  type="number"
                  name="purchasePrice"
                  value={formData.purchasePrice || ''}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
          </div>
        </details>

        {/* Maintenance Settings - Collapsible */}
        <details className="border border-gray-200 rounded-lg">
          <summary className="px-4 py-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Maintenance Settings (Optional)</h3>
            <span className="text-gray-500 text-sm">Click to expand</span>
          </summary>
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Service Interval (Miles)
              </label>
              <input
                type="number"
                name="serviceIntervalMiles"
                value={formData.serviceIntervalMiles}
                onChange={handleInputChange}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Service Interval (Days)
              </label>
              <input
                type="number"
                name="serviceIntervalDays"
                value={formData.serviceIntervalDays}
                onChange={handleInputChange}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          </div>
        </details>

        {/* Insurance & Registration - Collapsible */}
        <details className="border border-gray-200 rounded-lg">
          <summary className="px-4 py-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Insurance & Registration (Optional)</h3>
            <span className="text-gray-500 text-sm">Click to expand</span>
          </summary>
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Insurance Provider
              </label>
              <input
                type="text"
                name="insuranceProvider"
                value={formData.insuranceProvider}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., State Farm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Insurance Policy Number
              </label>
              <input
                type="text"
                name="insurancePolicyNumber"
                value={formData.insurancePolicyNumber}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Insurance Expiry Date
              </label>
              <input
                type="date"
                name="insuranceExpiryDate"
                value={formatDateForInput(formData.insuranceExpiryDate)}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Registration Number
              </label>
              <input
                type="text"
                name="registrationNumber"
                value={formData.registrationNumber}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Registration Expiry Date
              </label>
              <input
                type="date"
                name="registrationExpiryDate"
                value={formatDateForInput(formData.registrationExpiryDate)}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          </div>
        </details>

        {/* Form Actions */}
        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : vehicle ? 'Update Vehicle' : 'Add Vehicle'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default VehicleForm;